import { motion } from 'framer-motion'
import clsx from 'clsx'
import { useId } from 'react'
import type { Category, EvolutionTier } from '@/milestones/types'
import {
  SparkleRing,
  FireLayer,
  LightningLayer,
  FireworksLayer,
  GradientHaloLayer,
} from './TrophyEffects'

interface Props {
  category: Category
  tier: EvolutionTier
  size?: number
  label?: string
  
  static_?: boolean
}

interface RenderProps {
  size: number
  tier: EvolutionTier
  gradientId: string
  shadowId: string
  strokeOnly: boolean
  flatColor?: string
}

const CATEGORY_RENDER: Record<Category, (props: RenderProps) => JSX.Element> = {
  growth: ({ size, tier, gradientId, shadowId, strokeOnly, flatColor }) => {
    const fill = strokeOnly ? 'none' : (flatColor ?? `url(#${gradientId})`)
    const stroke = strokeOnly ? 'currentColor' : 'none'
    const sw = strokeOnly ? size * 0.03 : 0
    const arrowColor = strokeOnly ? 'currentColor' : '#ffffff'
    return (
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 size-full">
        {tier >= 1 && (
          <ellipse
            cx={size / 2}
            cy={size * 0.88}
            rx={size * 0.34}
            ry={size * 0.05}
            fill={`url(#${shadowId})`}
          />
        )}
        {}
        <rect
          x={size * 0.16}
          y={size * 0.58}
          width={size * 0.14}
          height={size * 0.26}
          rx={size * 0.025}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
        <rect
          x={size * 0.43}
          y={size * 0.42}
          width={size * 0.14}
          height={size * 0.42}
          rx={size * 0.025}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
        <rect
          x={size * 0.7}
          y={size * 0.24}
          width={size * 0.14}
          height={size * 0.6}
          rx={size * 0.025}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
        {}
        <path
          d={`M ${size * 0.18} ${size * 0.52} Q ${size * 0.42} ${size * 0.34}, ${size * 0.62} ${size * 0.3} T ${size * 0.9} ${size * 0.14}`}
          stroke={arrowColor}
          strokeWidth={size * 0.045}
          strokeLinecap="round"
          fill="none"
          opacity={strokeOnly ? 1 : 0.95}
        />
        <path
          d={`M ${size * 0.82} ${size * 0.12} L ${size * 0.94} ${size * 0.1} L ${size * 0.86} ${size * 0.22}`}
          stroke={arrowColor}
          strokeWidth={size * 0.045}
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
          opacity={strokeOnly ? 1 : 0.95}
        />
        {tier >= 2 && (
          <rect
            x={size * 0.72}
            y={size * 0.26}
            width={size * 0.04}
            height={size * 0.55}
            rx={size * 0.01}
            fill="#ffffff"
            opacity={0.3}
          />
        )}
      </svg>
    )
  },
  reliability: ({ size, tier, gradientId, shadowId, strokeOnly, flatColor }) => {
    const cx = size / 2
    const fill = strokeOnly ? 'none' : (flatColor ?? `url(#${gradientId})`)
    const stroke = strokeOnly ? 'currentColor' : 'none'
    const sw = strokeOnly ? size * 0.03 : 0
    const checkColor = strokeOnly ? 'currentColor' : '#ffffff'
    return (
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 size-full">
        {tier >= 1 && (
          <ellipse
            cx={cx}
            cy={size * 0.9}
            rx={size * 0.32}
            ry={size * 0.05}
            fill={`url(#${shadowId})`}
          />
        )}
        <path
          d={`M ${cx} ${size * 0.16}
              L ${cx + size * 0.32} ${size * 0.28}
              V ${size * 0.52}
              C ${cx + size * 0.32} ${size * 0.7} ${cx + size * 0.12} ${size * 0.82} ${cx} ${size * 0.86}
              C ${cx - size * 0.12} ${size * 0.82} ${cx - size * 0.32} ${size * 0.7} ${cx - size * 0.32} ${size * 0.52}
              V ${size * 0.28} Z`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        {tier >= 2 && (
          <path
            d={`M ${cx} ${size * 0.22}
                L ${cx + size * 0.26} ${size * 0.32}
                V ${size * 0.52}
                C ${cx + size * 0.26} ${size * 0.66} ${cx + size * 0.1} ${size * 0.76} ${cx} ${size * 0.8}
                C ${cx - size * 0.1} ${size * 0.76} ${cx - size * 0.26} ${size * 0.66} ${cx - size * 0.26} ${size * 0.52}
                V ${size * 0.32} Z`}
            fill="#ffffff"
            opacity={0.14}
          />
        )}
        <path
          d={`M ${cx - size * 0.13} ${size * 0.5}
              L ${cx - size * 0.03} ${size * 0.6}
              L ${cx + size * 0.16} ${size * 0.38}`}
          stroke={checkColor}
          strokeWidth={size * 0.05}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity={strokeOnly ? 1 : 0.95}
        />
      </svg>
    )
  },
  revenue: ({ size, tier, gradientId, shadowId, strokeOnly, flatColor }) => {
    const cx = size / 2
    const cy = size / 2
    const fill = strokeOnly ? 'none' : (flatColor ?? `url(#${gradientId})`)
    const stroke = strokeOnly ? 'currentColor' : 'none'
    const sw = strokeOnly ? size * 0.03 : 0
    const textColor = strokeOnly ? 'currentColor' : '#ffffff'
    return (
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 size-full">
        {tier >= 1 && (
          <ellipse
            cx={cx}
            cy={size * 0.88}
            rx={size * 0.32}
            ry={size * 0.05}
            fill={`url(#${shadowId})`}
          />
        )}
        <circle cx={cx} cy={cy} r={size * 0.34} fill={fill} stroke={stroke} strokeWidth={sw} />
        {tier >= 2 && (
          <>
            <circle
              cx={cx}
              cy={cy}
              r={size * 0.27}
              fill="none"
              stroke="#ffffff"
              strokeWidth={size * 0.018}
              opacity={0.38}
            />
            <ellipse
              cx={cx - size * 0.05}
              cy={cy - size * 0.18}
              rx={size * 0.07}
              ry={size * 0.04}
              fill="#ffffff"
              opacity={0.5}
            />
          </>
        )}
        <text
          x={cx}
          y={cy + size * 0.13}
          textAnchor="middle"
          fontSize={size * 0.42}
          fontWeight="800"
          fill={textColor}
          fontFamily="Inter Tight"
        >
          $
        </text>
      </svg>
    )
  },
  speed: ({ size, tier, gradientId, shadowId, strokeOnly, flatColor }) => {
    const cx = size / 2
    const fill = strokeOnly ? 'none' : (flatColor ?? `url(#${gradientId})`)
    const stroke = strokeOnly ? 'currentColor' : 'none'
    const sw = strokeOnly ? size * 0.03 : 0
    const accent = strokeOnly ? 'currentColor' : '#ffffff'
    return (
      <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 size-full">
        {tier >= 1 && (
          <ellipse
            cx={cx}
            cy={size * 0.9}
            rx={size * 0.3}
            ry={size * 0.045}
            fill={`url(#${shadowId})`}
          />
        )}
        <path
          d={`M ${cx + size * 0.05} ${size * 0.12}
              L ${cx - size * 0.22} ${size * 0.52}
              L ${cx - size * 0.02} ${size * 0.52}
              L ${cx - size * 0.1} ${size * 0.88}
              L ${cx + size * 0.24} ${size * 0.42}
              L ${cx + size * 0.04} ${size * 0.42} Z`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        {tier >= 2 && (
          <path
            d={`M ${cx + size * 0.02} ${size * 0.18}
                L ${cx - size * 0.13} ${size * 0.46}
                L ${cx - size * 0.02} ${size * 0.46}
                L ${cx - size * 0.07} ${size * 0.74}`}
            stroke={accent}
            strokeWidth={size * 0.025}
            strokeLinecap="round"
            fill="none"
            opacity={0.55}
          />
        )}
      </svg>
    )
  },
}

const TIER_PALETTE: Record<
  EvolutionTier,
  {
    top: string
    mid: string
    bottom: string
    flat: string
    glow: string
    strokeOnly?: boolean
    flatColor?: string
  }
> = {
  0: {
    top: '#8b8ba0',
    mid: '#8b8ba0',
    bottom: '#8b8ba0',
    flat: '#8b8ba0',
    glow: 'transparent',
    strokeOnly: true,
  },
  1: {
    top: '#c98b4a',
    mid: '#b06432',
    bottom: '#8e4a1c',
    flat: '#b06432',
    glow: 'transparent',
    flatColor: '#b06432',
  },
  2: { top: '#d6dce6', mid: '#a4b1c0', bottom: '#7c8a9c', flat: '#a4b1c0', glow: '#a4b1c044' },
  3: { top: '#ffd87a', mid: '#ffb45b', bottom: '#ff5bc8', flat: '#ffb45b', glow: '#ffb45b99' },
  4: { top: '#5b6bff', mid: '#9e5bff', bottom: '#ff5bc8', flat: '#9e5bff', glow: '#9e5bffbb' },
  5: { top: '#00e5ff', mid: '#9e5bff', bottom: '#ff5bc8', flat: '#00e5ff', glow: '#00e5ffdd' },
}

export function EvolvingTrophy({ category, tier, size = 80, label, static_ = false }: Props) {
  const gradId = useId().replace(/:/g, '')
  const shadowId = `shadow-${gradId}`
  const fillGradId = `fill-${gradId}`
  const palette = TIER_PALETTE[tier]
  const Render = CATEGORY_RENDER[category]
  const rotateClass =
    static_ || tier <= 1
      ? ''
      : tier === 2
        ? 'trophy-rotate-slow'
        : tier <= 4
          ? 'trophy-rotate-medium'
          : 'trophy-rotate-fast'
  const intensity = tier / 5

  return (
    <div className="flex flex-col items-center gap-1.5" style={{ color: palette.flat }}>
      <div className="trophy-stage relative" style={{ width: size, height: size }}>
        <svg width="0" height="0" className="absolute" aria-hidden="true">
          <defs>
            <linearGradient id={fillGradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={palette.top} />
              <stop offset="55%" stopColor={palette.mid} />
              <stop offset="100%" stopColor={palette.bottom} />
            </linearGradient>
            <radialGradient id={shadowId} cx="50%" cy="50%">
              <stop offset="0%" stopColor="#000000" stopOpacity={tier <= 1 ? 0.18 : 0.38} />
              <stop offset="100%" stopColor="#000000" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>

        {tier >= 3 && !static_ && <GradientHaloLayer size={size} intensity={intensity} />}
        {tier >= 5 && !static_ && <FireworksLayer size={size} intensity={intensity} />}
        {tier >= 4 && !static_ && <LightningLayer size={size} intensity={intensity} />}
        {tier >= 4 && !static_ && <FireLayer size={size} intensity={intensity} />}

        <motion.div
          className={clsx('absolute inset-0', rotateClass, tier >= 2 && !static_ && 'trophy-bob')}
          style={{ transformOrigin: '50% 60%', willChange: 'transform' }}
        >
          <Render
            size={size}
            tier={tier}
            gradientId={fillGradId}
            shadowId={shadowId}
            strokeOnly={!!palette.strokeOnly}
            flatColor={palette.flatColor}
          />
        </motion.div>

        {tier >= 3 && !static_ && (
          <SparkleRing size={size} count={tier >= 4 ? 12 : 8} intensity={intensity} />
        )}

        {tier >= 3 && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              boxShadow: `0 0 ${10 + tier * 7}px ${palette.glow}, inset 0 0 ${6 + tier * 3}px ${palette.glow}88`,
            }}
          />
        )}
      </div>
      {label && (
        <span className="text-[10px] font-medium uppercase tracking-widest text-ink-subtle">
          {label}
        </span>
      )}
    </div>
  )
}
